import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { Trigger } from '@flipster/react/misc/browser/intersection';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import { Table, TableCell, TableHeader, TableRow } from '@flipster/ui/Table';
import { DateFormatter } from '@flipster/utils/datetime';

import { usd1PointsHistoryBunja } from '../bunja';

const threshold = 5;

const PointsHistory = () => {
  const { usd1PointsHistoryAtom, isLoadingAtom, isFetchingNextPageAtom, hasNextPageAtom, fetchNextPageAtom } =
    useBunja(usd1PointsHistoryBunja);

  const items = useAtomValue(usd1PointsHistoryAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const isFetchingNextPage = useAtomValue(isFetchingNextPageAtom);
  const hasNextPage = useAtomValue(hasNextPageAtom);
  const fetchNextPage = useSetAtom(fetchNextPageAtom);

  return (
    <Table
      isLoading={isLoading}
      className={css({
        '& th:first-child': { pl: '0!' },
        '& th:last-child': { pr: '0!' },
        '& td:first-child': { pl: '0!' },
        '& td:last-child': { pr: '0!' },
      })}
      tHeader={
        <TableHeader
          bold={false}
          columns={[
            { title: 'Date', width: 95 },
            { title: 'Earned from', width: 164 },
            { title: 'USD1 Points', width: 77, align: 'right' },
          ]}
        />
      }
      items={items}
      renderRow={(item, index) => {
        const shouldPrefetch = index === items.length - threshold && hasNextPage && !isFetchingNextPage;
        return (
          <React.Fragment key={item.id}>
            {shouldPrefetch && <Trigger onTrigger={fetchNextPage} />}
            <TableRow>
              <TableCell>
                <div className={vstack({ gap: 4, color: 'content.default.level-2' })}>
                  <Datetime
                    css={{ textStyle: 'Text/L/Monospace/Regular' }}
                    value={item.createdAt}
                    format={DateFormatter.Date}
                    isUTC
                  />
                  <Datetime
                    css={{ textStyle: 'Text/M/Monospace/Regular' }}
                    value={item.createdAt}
                    format={DateFormatter.TimeSec}
                    isUTC
                    withUTCText
                  />
                </div>
              </TableCell>
              <TableCell>
                <div className={vstack({ gap: 4, alignItems: 'flex-start' })}>
                  <div className={css({ textStyle: 'Text/L/Strong', textTransform: 'capitalize' })}>
                    {item.pointsType.toLowerCase()}
                  </div>
                  {item.pointsType === 'TRADING' && (
                    <div className={css({ textStyle: 'Text/M/Regular', color: 'content.accent.promotion' })}>
                      Flipster bonus included
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell align='right' className={css({ textStyle: 'Text/L/Regular' })}>
                <Formatted.Number value={item.totalPoints} withComma />
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      }}
    />
  );
};

export default PointsHistory;
